import { AxiosError } from 'axios'
import { useQuery } from '@tanstack/react-query'
import {
  ClassesResponse,
  ClassParams,
  Class,
  ListYears,
  ListTutors,
  ClassByModule,
} from '@/types/class.type'
import { Statistics } from '@/types/statistics'
import { ClassTestRes } from '@/types/test.type'
import { axiosInstance } from '@/lib/axios'
import BaseService from './baseService'

// interface ParamsProps {
//   module_id: string
//   limit: number
//   page: number
// }

interface Testparams {
  page: number
  classId: number
  isPracTest: string
  status: string
  creator_type: string
  search?: string
}

export const getClassByModule = async ({
  userId,
  moduleIds,
}: {
  userId: string
  moduleIds: number[]
}) => {
  const queryParams = new URLSearchParams()

  moduleIds.forEach((id) => {
    queryParams.append('module_ids', id.toString())
  })

  queryParams.append('user_id', userId)

  return axiosInstance.get<ClassByModule[]>(
    `/class/by-modules?${queryParams.toString()}`
  )
}

export const getClasses = async (params: ClassParams) => {
  try {
    const queryParams = new URLSearchParams()

    if (params.years && params.years.length > 0) {
      params.years.forEach((year) => {
        queryParams.append('years', year.toString())
      })
    }

    if (params.tutors && params.tutors.length > 0) {
      params.tutors.forEach((tutor) => {
        queryParams.append('tutors', tutor.toString())
      })
    }

    if (params.year_asc !== undefined) {
      queryParams.append('year_asc', params.year_asc.toString())
    }

    if (params.is_active !== undefined) {
      queryParams.append('is_active', params.is_active.toString())
    }

    if (params.query && params.query.trim() !== '') {
      queryParams.append('query', params.query)
    }

    queryParams.append('page', params.page.toString())
    queryParams.append('size', params.size.toString())

    if (params.module_id && params.module_id !== '0') {
      queryParams.append('module_id', params.module_id.toString())
    }

    const queryString = queryParams.toString()
    const url = `/class/with-counts${queryString ? `?${queryString}` : ''}`

    return axiosInstance.get<ClassesResponse>(url)
  } catch (error) {
    if (error instanceof AxiosError && error.response?.data) {
      throw new Error(error.response.data.message || 'Failed to fetch classes')
    }
    throw error
  }
}

export const getClassTests = async ({
  page,
  classId,
  isPracTest,
  status,
  search,
  creator_type,
}: Testparams) => {
  const params: Record<string, string | number> = {
    page,
    limit: 10,
    is_practice: isPracTest,
    status: status.toLocaleLowerCase(),
    creator_type,
  }

  if (search && search.trim() !== '') {
    params.search = search
  }

  return axiosInstance.get<ClassTestRes>(`/class/${classId}/practice-tests`, {
    params,
  })
}

export const getListClassYears = async (moduleId?: number) => {
  try {
    const response = await axiosInstance.get<ListYears>(
      `/class/years`,
      moduleId
        ? {
            params: {
              module_id: moduleId,
            },
          }
        : {}
    )
    return response
  } catch (error) {
    if (error instanceof AxiosError && error.response?.data) {
      throw new Error(
        error.response.data.message || 'Failed to fetch list classes years'
      )
    }
    throw error
  }
}

export const getListClassTutors = async (moduleId?: number) => {
  try {
    const response = await axiosInstance.get<ListTutors>(
      `/class/tutors`,
      moduleId
        ? {
            params: {
              module_id: moduleId,
            },
          }
        : {}
    )
    return response
  } catch (error) {
    if (error instanceof AxiosError && error.response?.data) {
      throw new Error(
        error.response.data.message || 'Failed to fetch list classes tutors'
      )
    }
    throw error
  }
}

export const useGetClassByModules = ({
  userId,
  moduleIds,
}: {
  userId: string
  moduleIds: number[]
}) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['class_by_module', userId, moduleIds],
    queryFn: () => getClassByModule({ userId, moduleIds }),
    enabled: !!userId,
  })

  return {
    classesByModule: data?.data,
    isLoadingClass: isLoading,
    reFreshClass: refetch,
  }
}

export const useGetClasses = ({
  module_id,
  page,
  size,
  is_active,
  query,
  years,
  year_asc,
  tutors,
}: ClassParams) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      'classes',
      module_id,
      page,
      size,
      is_active,
      query,
      years,
      year_asc,
      tutors,
    ],
    queryFn: () =>
      getClasses({
        module_id,
        page,
        size,
        is_active,
        query,
        years,
        year_asc,
        tutors,
      }),
    enabled: !!module_id,
  })

  return {
    classes: data?.data.data,
    meta: data?.data.meta,
    isLoadingClasses: isLoading,
    refetchClasses: refetch,
  }
}

export const useGetClassTest = ({
  page,
  classId,
  isPracTest,
  status,
  search,
  creator_type,
}: Testparams) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      'classTest',
      page,
      classId,
      isPracTest,
      status,
      search,
      creator_type,
    ],
    queryFn: () =>
      getClassTests({
        page,
        classId,
        isPracTest,
        status,
        search,
        creator_type,
      }),
    enabled: !!classId,
  })

  return {
    tests: data?.data.data,
    meta: data?.data.meta,
    isLoadingTest: isLoading,
    refetchTest: refetch,
  }
}

export const useGetListClassYears = (moduleId?: number) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['classYears', moduleId],
    queryFn: () => getListClassYears(moduleId),
  })
  return {
    classYears: data?.data.years,
    isLoadingClassYears: isLoading,
    refetchClassYears: refetch,
  }
}

export const useGetListClassTutors = (moduleId?: number) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['classTutors', moduleId],
    queryFn: () => getListClassTutors(moduleId),
  })
  return {
    classTutors: data?.data.tutors,
    isLoadingClassTutors: isLoading,
    refetchClassTutors: refetch,
  }
}

class ClassService extends BaseService {
  async getClasses(params: ClassParams): Promise<ClassesResponse> {
    try {
      // Filter out module_id if it's 0 or null
      const filteredParams: Partial<ClassParams> = { ...params }
      if (filteredParams.module_id === 0 || filteredParams.module_id === null) {
        delete filteredParams.module_id
      }

      const response = await this.get<ClassesResponse>(
        '/class/with-counts',
        filteredParams
      )
      return response
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(
          error.response.data.message || 'Failed to fetch classes'
        )
      }
      throw error
    }
  }

  async getStatisticsByClassId(classId: number): Promise<Statistics> {
    return this.get<Statistics>(`/class/${classId}/statistics`).then(
      (res) => res
    )
  }

  async getClassById(classId: number): Promise<Class> {
    try {
      const response = await this.get<Class>(`/class/${classId}`)
      return response
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw new Error(error.response.data.message || 'Failed to fetch class')
      }
      throw error
    }
  }
}

export const classService = new ClassService()
